/* CSidebar / CSidebarNav / CSidebarNavItem / CSidebarNavDropdown for Vue 3 (markup unchanged). */
import { h, resolveComponent } from 'vue'
import { CLink, props as linkProps } from './link'
import { CBadge } from './basic'
import CIcon from './CIcon.vue'
import CScrollbar from './CScrollbar.vue'
import { makeUid } from './utils'

export const CSidebar = {
  name: 'CSidebar',
  props: {
    fixed: { type: Boolean, default: true },
    unfoldable: Boolean,
    overlaid: Boolean,
    breakpoint: { type: [String, Boolean], default: 'lg' },
    minimize: Boolean,
    show: { type: [Boolean, String], default: 'responsive' },
    size: String,
    hideOnMobileClick: { type: Boolean, default: true },
    aside: Boolean,
    colorScheme: { type: String, default: 'dark' },
    dropdownMode: { type: String, default: 'openActive' }
  },
  emits: ['update:show'],
  provide () {
    const state = {}
    Object.defineProperty(state, 'minimize', { get: () => this.minimize && !this.unfoldable })
    return { sidebarState: state, dropdownMode: this.dropdownMode }
  },
  data () {
    return { open: this.show }
  },
  created () {
    this._backdropId = makeUid() + 'backdrop'
  },
  watch: {
    show (val) {
      this.open = val
    },
    open: {
      immediate: true,
      handler (val) {
        val === true ? this.createBackdrop() : this.removeBackdrop()
      }
    }
  },
  beforeUnmount () {
    this.removeBackdrop()
  },
  computed: {
    sidebarClasses () {
      const haveResponsiveClass = this.breakpoint && this.open === 'responsive'
      return [
        'c-sidebar',
        `c-sidebar-${this.colorScheme}`,
        {
          'c-sidebar-show': this.open === true,
          [`c-sidebar-${this.breakpoint}-show`]: haveResponsiveClass,
          'c-sidebar-fixed': this.fixed && !this.overlaid,
          'c-sidebar-right': this.aside,
          'c-sidebar-minimized': this.minimize && !this.unfoldable,
          'c-sidebar-unfoldable': this.minimize && this.unfoldable,
          'c-sidebar-overlaid': this.overlaid,
          [`c-sidebar-${this.size}`]: this.size
        }
      ]
    }
  },
  methods: {
    sidebarClick (e) {
      if (this.hideOnMobileClick && this.isOnMobile()) {
        if (e.target.closest('a.c-sidebar-nav-link')) this.closeSidebar()
      }
    },
    closeSidebar () {
      this.open = this.overlaid ? false : 'responsive'
      this.$emit('update:show', this.open)
    },
    isOnMobile () {
      return Boolean(getComputedStyle(this.$el).getPropertyValue('--is-mobile'))
    },
    sidebarCloseListener (e) {
      if (document.getElementById(this._backdropId) && !this.$el.contains(e.target)) this.closeSidebar()
    },
    createBackdrop () {
      const backdrop = document.createElement('div')
      if (this.overlaid) {
        document.addEventListener('click', this.sidebarCloseListener, true)
      } else {
        backdrop.addEventListener('click', this.closeSidebar)
      }
      backdrop.className = 'c-sidebar-backdrop c-show'
      backdrop.id = this._backdropId
      document.body.appendChild(backdrop)
    },
    removeBackdrop () {
      const backdrop = document.getElementById(this._backdropId)
      if (backdrop) {
        document.removeEventListener('click', this.sidebarCloseListener, true)
        backdrop.removeEventListener('click', this.closeSidebar)
        document.body.removeChild(backdrop)
      }
    }
  },
  render () {
    return h('div', { class: this.sidebarClasses, onClick: this.sidebarClick }, this.$slots.default ? this.$slots.default() : undefined)
  }
}

export const CSidebarNav = {
  name: 'CSidebarNav',
  inject: { sidebarState: { default: { minimize: false } } },
  computed: {
    psSettings () {
      return {
        maxScrollbarLength: 200,
        minScrollbarLength: 40,
        suppressScrollX: getComputedStyle(document.querySelector('html')).direction !== 'rtl',
        wheelPropagation: false
      }
    }
  },
  render () {
    return h(CScrollbar, {
      class: 'c-sidebar-nav h-100',
      settings: this.psSettings,
      switcher: !this.sidebarState.minimize,
      tag: 'ul'
    }, { default: () => (this.$slots.default ? this.$slots.default() : undefined) })
  }
}

function computeIcon (icon) {
  if (typeof icon === 'object') {
    const key = icon.size ? 'class' : 'customClasses'
    return { ...icon, [key]: icon.customClasses || ['c-sidebar-nav-icon', icon.className] }
  }
  return { customClasses: 'c-sidebar-nav-icon', name: icon }
}

export const CSidebarNavItem = {
  name: 'CSidebarNavItem',
  props: {
    ...linkProps,
    exact: { type: Boolean, default: true },
    activeClass: { type: String, default: 'c-active' },
    name: String,
    icon: [String, Object],
    fontIcon: String,
    badge: Object,
    addLinkClasses: [String, Array, Object],
    label: Boolean,
    color: String
  },
  render () {
    const linkPropsOut = Object.keys(linkProps).reduce((o, k) => { o[k] = this[k]; return o }, {})
    const content = () => [
      this.icon ? h(CIcon, computeIcon(this.icon)) : null,
      this.fontIcon ? h('i', { class: ['c-sidebar-nav-icon', this.fontIcon] }) : null,
      this.name,
      this.badge ? h(CBadge, Object.assign({}, this.badge, { text: null }), { default: () => this.badge.text }) : null
    ]
    return h('li', { class: 'c-sidebar-nav-item' }, this.$slots.default ? this.$slots.default() : [
      h(CLink, {
        class: [this.label ? 'c-sidebar-nav-label' : 'c-sidebar-nav-link', this.color && `c-sidebar-nav-link-${this.color}`, this.addLinkClasses],
        ...linkPropsOut
      }, { default: content })
    ])
  }
}

export const CSidebarNavDropdown = {
  name: 'CSidebarNavDropdown',
  props: {
    name: String,
    route: String,
    icon: [String, Object],
    fontIcon: String,
    show: Boolean,
    items: Array
  },
  emits: ['update:show', 'item-clicked'],
  inject: { dropdownMode: { default: 'openActive' } },
  data () {
    return { open: this.show }
  },
  watch: {
    show (val) {
      this.open = val
    },
    $route: {
      immediate: true,
      handler (route) {
        if (!route) return
        const mode = this.dropdownMode
        if (mode === 'close') {
          this.open = false
        } else if (mode === 'closeInactive' && this.route) {
          this.open = route.fullPath.includes(this.route)
        } else if (mode === 'openActive' && !this.open && this.route) {
          this.open = route.fullPath.includes(this.route)
        }
      }
    }
  },
  mounted () {
    // TheSidebar's accordion logic reads/writes `li.__vue__.open` (Vue 2 exposed the instance on the element)
    this.$el.__vue__ = this
  },
  updated () {
    if (this.$el) this.$el.__vue__ = this
  },
  methods: {
    handleClick (e) {
      e.preventDefault()
      this.open = !this.open
      this.$emit('update:show', this.open)
    },
    itemClicked (e) {
      this.$emit('item-clicked', e)
    }
  },
  render () {
    return h('li', { class: ['c-sidebar-nav-dropdown', { 'c-show': this.open }] }, [
      h('a', { class: 'c-sidebar-nav-dropdown-toggle', onClick: this.handleClick }, [
        this.icon ? h(CIcon, computeIcon(this.icon)) : null,
        this.fontIcon ? h('i', { class: ['c-sidebar-nav-icon', this.fontIcon] }) : null,
        this.name
      ]),
      h('ul', { class: 'c-sidebar-nav-dropdown-items', onClick: this.itemClicked },
        this.$slots.default ? this.$slots.default() : (this.items || []).map((item, key) => h(CSidebarNavItem, { ...item, key }))
      )
    ])
  }
}

/* CRenderFunction: renders the `_name/_children` nav config (CoreUI v3 format). */
const RENDER_OPTIONS = ['attrs', 'class', 'style', 'key', 'ref', 'on', 'nativeOn', 'domProps']
export const CRenderFunction = {
  name: 'CRenderFunction',
  props: { contentToRender: Array, flat: Boolean },
  methods: {
    toVNode (item) {
      if (typeof item === 'string') return item
      if (!item || typeof item !== 'object') return null
      const name = item._name || 'div'
      let type = name
      if (/^[A-Z]/.test(name) || name.includes('-')) {
        const known = { CSidebarNav, CSidebarNavItem, CSidebarNavDropdown, CSidebarNavTitle: resolveComponent('CSidebarNavTitle') }
        type = known[name] || resolveComponent(name)
      }
      const data = {}
      Object.keys(item).forEach((key) => { if (!key.includes('_')) data[key] = item[key] })
      RENDER_OPTIONS.forEach((opt) => {
        const v = item[`_${opt}`]
        if (!v) return
        if (opt === 'attrs' || opt === 'domProps') Object.assign(data, v)
        else if (opt === 'on' || opt === 'nativeOn') Object.keys(v).forEach((ev) => { data['on' + ev.charAt(0).toUpperCase() + ev.slice(1)] = v[ev] })
        else data[opt] = v
      })
      const children = item._children ? item._children.map((c) => this.toVNode(c)) : null
      if (typeof type === 'string') return h(type, data, children)
      return h(type, data, children ? { default: () => children } : undefined)
    }
  },
  render () {
    const content = this.contentToRender || []
    if (!content.length) return null
    // `flat` mode (used by the sidebar): render the first top-level item
    if (this.flat) return this.toVNode(content[0])
    return h('div', null, content.map((c) => this.toVNode(c)))
  }
}
